import { X } from 'lucide-react';
import type { FocusFilters } from './search';
import type { AdNetworkAccount } from './api';
import { ASSET_TYPE_LABEL, STATUS_FILTER_LABEL, accountLabel, networkGroups, osOptionLabel } from './filterOptions';
import { formatCountry, formatInt, formatMoney } from '@/lib/format';

type Chip = { id: string; label: string; value: string; keys: (keyof FocusFilters)[] };

const summary = (values: string[]): string => {
  if (values.length <= 2) return values.join(', ');
  return `${values.slice(0, 2).join(', ')} +${values.length - 2}`;
};

/** One chip per active filter, in the order the panel lists them. */
function activeFilterChips(f: FocusFilters, accounts: readonly AdNetworkAccount[]): Chip[] {
  const chips: Chip[] = [];
  const list = (id: string, label: string, values: string[], keys: (keyof FocusFilters)[]) => {
    if (values.length) chips.push({ id, label, value: summary(values), keys });
  };
  list('status', 'Status', f.status.map((s) => STATUS_FILTER_LABEL[s]), ['status']);

  if (f.ad_network_account_id.length) {
    // A network whose every account is selected reads as the network.
    const selected = new Set(f.ad_network_account_id);
    const names: string[] = [];
    let wholeNetworks = true;
    const known = new Set<string>();
    for (const group of networkGroups(accounts)) {
      const picked = group.accountIds.filter((id) => selected.has(id));
      picked.forEach((id) => known.add(id));
      if (!picked.length) continue;
      if (picked.length === group.accountIds.length) names.push(group.name);
      else {
        wholeNetworks = false;
        names.push(...picked.map((id) => accountLabel(accounts.find((a) => a.id === id) ?? { id, type: '', name: null, enabled: true, deleted: false })));
      }
    }
    const unknown = f.ad_network_account_id.filter((id) => !known.has(id));
    if (unknown.length) wholeNetworks = false;
    list('accounts', wholeNetworks ? 'Network' : 'Account', [...names, ...unknown], ['ad_network_account_id']);
  }

  list('campaign', 'Campaign', f.campaign_name, ['campaign_name']);
  list('ad_group', 'Ad group', f.ad_group_name, ['ad_group_name']);
  list('ad', 'Ad', f.ad_group_ad_name, ['ad_group_ad_name']);
  list('type', 'Type', f.asset_type.map((t) => ASSET_TYPE_LABEL[t]), ['asset_type']);
  list('country', 'Country', f.country_code.map((c) => formatCountry(c)), ['country_code']);
  list('placement', 'Placement', f.platform, ['platform']);
  list('os', 'OS', f.os.map(osOptionLabel), ['os']);
  if (f.active_during_past_n_hours) chips.push({ id: 'active', label: 'Activity', value: 'Impressions in the last 24 hours', keys: ['active_during_past_n_hours'] });
  if (f.min_impressions) chips.push({ id: 'min_impressions', label: 'Impressions', value: `≥ ${formatInt(f.min_impressions)}`, keys: ['min_impressions'] });
  if (f.min_clicks) chips.push({ id: 'min_clicks', label: 'Clicks', value: `≥ ${formatInt(f.min_clicks)}`, keys: ['min_clicks'] });
  if (f.min_installs) chips.push({ id: 'min_installs', label: 'Installs', value: `≥ ${formatInt(f.min_installs)}`, keys: ['min_installs'] });
  if (f.min_cost) chips.push({ id: 'min_cost', label: 'Spend', value: `≥ ${formatMoney(f.min_cost, 'table')}`, keys: ['min_cost'] });
  return chips;
}

/** The applied filters as removable chips beside the Filters button. */
export function ActiveFilterChips({ filters, accounts, onRemove }: { filters: FocusFilters; accounts: readonly AdNetworkAccount[]; onRemove: (keys: (keyof FocusFilters)[]) => void }) {
  const chips = activeFilterChips(filters, accounts);
  if (!chips.length) return null;
  return (
    <ul aria-label="Active filters" className="m-0 flex min-w-0 flex-[1_1_240px] list-none flex-wrap items-center gap-1.5 p-0">
      {chips.map((chip) => (
        <li key={chip.id} className="inline-flex h-8 max-w-[320px] items-center gap-1 rounded-ctl border border-line bg-surface pr-1 pl-2.5 text-sm">
          <span className="min-w-0 truncate">
            {chip.label} <b className="font-medium">{chip.value}</b>
          </span>
          <button
            type="button"
            aria-label={`Remove ${chip.label} filter`}
            onClick={() => { onRemove(chip.keys); }}
            className="grid size-6 shrink-0 place-items-center rounded-full border-0 bg-transparent text-muted hover:bg-wash hover:text-fg"
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
}
